import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Clock, CheckCircle, XCircle, Eye, Film, Inbox } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-700',
  reviewed: 'bg-yellow-100 text-yellow-700',
  contacted: 'bg-green-100 text-green-700',
  closed: 'bg-gray-100 text-gray-500',
};

const STATUS_ICONS = {
  new: Clock,
  reviewed: Eye,
  contacted: CheckCircle,
  closed: XCircle,
};

const STATUS_LIST = ['new', 'reviewed', 'contacted', 'closed'];

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
}

const FILM_STATUS_COLORS = {
  Lead: 'bg-gray-100 text-gray-600',
  Paid: 'bg-blue-100 text-blue-700',
  'In Review': 'bg-yellow-100 text-yellow-700',
  Delivered: 'bg-green-100 text-green-700',
  Closed: 'bg-gray-100 text-gray-500',
};

const FILM_STATUSES = ['Lead', 'Paid', 'In Review', 'Delivered', 'Closed'];

function FilmAuditDashboard() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const fetchSubs = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/film-audit/submissions`);
      setSubs(res.data);
    } catch { /* empty */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSubs(); }, [fetchSubs]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API_URL}/api/film-audit/submissions/${id}/status?status=${status}`);
      setSubs((prev) => prev.map((s) => (s.id === id ? { ...s, status } : s)));
      if (selected?.id === id) setSelected((prev) => ({ ...prev, status }));
    } catch { /* empty */ }
  };

  if (loading) return <p className="text-center text-gray-400 py-20">Loading...</p>;
  if (subs.length === 0) return <p className="text-center text-gray-400 py-20">No film audit submissions yet.</p>;

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-1 space-y-3 max-h-[75vh] overflow-y-auto pr-2">
        {subs.map((s) => (
          <div key={s.id} onClick={() => setSelected(s)}
            className={`bg-white rounded-xl p-4 cursor-pointer border-2 transition-all hover:shadow-md ${selected?.id === s.id ? 'border-blue-600 shadow-md' : 'border-transparent'}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="font-semibold text-gray-900">{s.player_name}</p>
                <p className="text-sm text-gray-500">{s.package_name} — ${s.amount_paid}</p>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full font-medium ${FILM_STATUS_COLORS[s.status] || 'bg-gray-100 text-gray-500'}`}>{s.status}</span>
            </div>
            <p className="text-xs text-gray-400 mt-2">{s.current_school} · {s.primary_position} · {s.graduation_year}</p>
          </div>
        ))}
      </div>
      <div className="lg:col-span-2">
        {selected ? (
          <Card className="border-0 shadow-lg">
            <CardContent className="p-8">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{selected.player_name}</h2>
                  <p className="text-gray-500">{selected.player_email} · {selected.player_phone}</p>
                  <p className="text-sm text-blue-600 font-semibold mt-1">{selected.package_name} — ${selected.amount_paid}</p>
                </div>
                <select value={selected.status} onChange={(e) => updateStatus(selected.id, e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
                  {FILM_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="grid md:grid-cols-2 gap-x-8 gap-y-4">
                {[
                  ['School', selected.current_school], ['City/State', selected.city_state],
                  ['Position', selected.primary_position], ['Class', selected.graduation_year],
                  ['Height/Weight', `${selected.height || '—'} / ${selected.weight || '—'}`],
                  ['GPA', selected.gpa], ['Parent', selected.parent_name], ['Parent Email', selected.parent_email],
                  ['Hudl', selected.hudl_link], ['Highlight', selected.highlight_link],
                  ['Level Believes', selected.level_believe], ['Level Hoping', selected.level_hoping],
                  ['Biggest Strength', selected.biggest_strength], ['Biggest Weakness', selected.biggest_weakness],
                  ['Evaluation Goals', selected.evaluation_goals], ['Need Help Most', selected.need_help_most],
                ].map(([label, val]) => (
                  <div key={label}>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">{label}</p>
                    {(label === 'Hudl' || label === 'Highlight') && val ? (
                      <a href={val} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline text-sm break-all">{val}</a>
                    ) : (
                      <p className="text-gray-900 text-sm">{val || '—'}</p>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-6">Submitted: {new Date(selected.created_at).toLocaleString()}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-400 py-20">Select a submission to view details</div>
        )}
      </div>
    </div>
  );
}

function DetailField({ label, val }) {
  if (label === 'Film Link' && val) {
    return (
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">{label}</p>
        <a href={val} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline text-sm break-all">{val}</a>
      </div>
    );
  }
  return (
    <div>
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">{label}</p>
      <p className="text-gray-900 text-sm">{val || '—'}</p>
    </div>
  );
}

function SubmissionDetail({ selected, onStatusChange }) {
  if (!selected) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400 py-20">
        Select a submission to view details
      </div>
    );
  }

  const fields = [
    ['Service', selected.service_requested],
    ['Role', selected.role],
    ['Team / Org', selected.team_or_org],
    ['Competitive Level', selected.competitive_level],
    ['Timeline', selected.timeline],
    ['How Heard', selected.how_heard],
    ['Film Link', selected.film_link],
    ['Clips / Games', selected.num_clips],
  ];

  return (
    <Card className="border-0 shadow-lg">
      <CardContent className="p-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{selected.name}</h2>
            <p className="text-gray-500">{selected.email} &middot; {selected.phone}</p>
          </div>
          <select
            data-testid="status-select"
            value={selected.status}
            onChange={(e) => onStatusChange(selected.id, e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value="new">New</option>
            <option value="reviewed">Reviewed</option>
            <option value="contacted">Contacted</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        <div className="grid md:grid-cols-2 gap-x-8 gap-y-5">
          {fields.map(([label, val]) => (
            <DetailField key={label} label={label} val={val} />
          ))}
        </div>

        <div className="mt-6">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Biggest Issue or Question</p>
          <p className="text-gray-900 text-sm leading-relaxed bg-gray-50 p-4 rounded-xl">{selected.biggest_issue || '—'}</p>
        </div>

        {selected.specific_plays && (
          <div className="mt-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Specific Plays / Concepts</p>
            <p className="text-gray-900 text-sm leading-relaxed bg-gray-50 p-4 rounded-xl">{selected.specific_plays}</p>
          </div>
        )}

        <p className="text-xs text-gray-400 mt-6">Submitted: {formatDate(selected.created_at)}</p>
      </CardContent>
    </Card>
  );
}

export default function Admin() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [activeTab, setActiveTab] = useState('film-audit');

  const fetchSubmissions = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/intake/submissions`);
      setSubmissions(res.data);
    } catch {
      // silently fail — dashboard shows empty state
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API_URL}/api/intake/submissions/${id}/status?status=${status}`);
      setSubmissions((prev) =>
        prev.map((s) => (s.id === id ? { ...s, status } : s))
      );
      if (selected?.id === id) setSelected((prev) => ({ ...prev, status }));
    } catch {
      // silently fail — status remains unchanged in UI
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img
              src="https://customer-assets.emergentagent.com/job_110eaf2c-e2da-4799-9c05-6d381eae4282/artifacts/13g1t9qc_BBEED318-098A-4FB8-8AEB-680648F471A0.jpeg"
              alt="WB Logo"
              className="h-10 w-10 object-contain"
            />
            <h1 className="text-xl font-bold text-gray-900">Intake Dashboard</h1>
          </div>
          <Link to="/">
            <Button variant="outline" className="rounded-full px-5 text-sm">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to Site
            </Button>
          </Link>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8 max-w-7xl">
        {/* Tab Switcher */}
        <div className="flex space-x-2 mb-8">
          <button onClick={() => { setActiveTab('film-audit'); setSelected(null); }}
            className={`flex items-center space-x-2 px-5 py-2.5 rounded-full font-semibold text-sm transition-all ${activeTab === 'film-audit' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}>
            <Film className="h-4 w-4" /><span>Film Audits</span>
          </button>
          <button onClick={() => { setActiveTab('general'); setSelected(null); }}
            className={`flex items-center space-x-2 px-5 py-2.5 rounded-full font-semibold text-sm transition-all ${activeTab === 'general' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}>
            <Inbox className="h-4 w-4" /><span>General Intake</span>
          </button>
        </div>

        {activeTab === 'film-audit' ? (
          <FilmAuditDashboard />
        ) : (
        <>
        {/* General Intake Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {STATUS_LIST.map((status) => {
            const count = submissions.filter((s) => s.status === status).length;
            const Icon = STATUS_ICONS[status];
            return (
              <Card key={status} className="border-0 shadow-sm">
                <CardContent className="p-5 flex items-center space-x-4">
                  <div className={`p-3 rounded-xl ${STATUS_COLORS[status]}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{count}</p>
                    <p className="text-xs text-gray-500 capitalize">{status}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {loading ? (
          <p className="text-center text-gray-400 py-20">Loading submissions...</p>
        ) : submissions.length === 0 ? (
          <p className="text-center text-gray-400 py-20">No submissions yet.</p>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* List */}
            <div className="lg:col-span-1 space-y-3 max-h-[75vh] overflow-y-auto pr-2">
              {submissions.map((s) => {
                const Icon = STATUS_ICONS[s.status] || Clock;
                return (
                  <div
                    key={s.id}
                    data-testid={`submission-row-${s.id}`}
                    onClick={() => setSelected(s)}
                    className={`bg-white rounded-xl p-4 cursor-pointer border-2 transition-all hover:shadow-md ${
                      selected?.id === s.id ? 'border-blue-600 shadow-md' : 'border-transparent'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-semibold text-gray-900">{s.name}</p>
                        <p className="text-sm text-gray-500 truncate">{s.service_requested}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium flex items-center space-x-1 ${STATUS_COLORS[s.status]}`}>
                        <Icon className="h-3 w-3" />
                        <span className="capitalize">{s.status}</span>
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">{formatDate(s.created_at)}</p>
                  </div>
                );
              })}
            </div>

            {/* Detail */}
            <div className="lg:col-span-2">
              <SubmissionDetail selected={selected} onStatusChange={updateStatus} />
            </div>
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
}
